import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  DataTable, DownloadButton, InfoHint, Panel, SelectMenu, StatusChip,
  type DataTableColumn,
} from '../../../../ui-kit';
import type { SimMove, SimPath, SimulatorPayload } from '../../../../types/plannerDashboard';
import { dollars, twoDecimal } from '../plannerFormat';

/** A column heading with its explanation behind an info icon, as on the other tables. */
function Heading({ label, info }: { label: string; info: string }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {label}
      <InfoHint title={info} label={`About ${label.toLowerCase()}`} />
    </Box>
  );
}

const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${dollars(Math.abs(n))}`;

interface SpendMovesPanelProps {
  payload: SimulatorPayload | null;
  loading?: boolean;
  onDownload: (pathKey: string) => void;
  downloading?: boolean;
  problem?: string | null;
}

/**
 * Where the money moves, for a scenario that moves the spend: which campaigns
 * get more money, which get less, and how much, a day. The rows come from the
 * server (simulator.spend_moves), the same list the download writes.
 *
 * Words follow "Writing: plain spoken English" in ../../CLAUDE.md.
 */
export default function SpendMovesPanel({ payload, loading, onDownload, downloading, problem }: SpendMovesPanelProps) {
  const moving = React.useMemo(() => (payload?.paths ?? []).filter((p) => p.moves.length > 0), [payload]);
  const [picked, setPicked] = React.useState<string>('');
  // Open on the first scenario that moves money and reaches the target; if
  // none does, on "Spend only".
  const fallback = moving.find((p) => p.reaches_goal) ?? moving[0] ?? null;
  const path: SimPath | null = moving.find((p) => p.key === picked) ?? fallback;
  if (!payload || !path) return null;

  const more = path.moves.filter((m) => m.direction === 'more');
  const less = path.moves.filter((m) => m.direction === 'less');
  const same = path.moves.filter((m) => m.direction === 'same');
  const outOf = less.reduce((n, m) => n - m.change, 0);
  const into = more.reduce((n, m) => n + m.change, 0);
  const today = payload.today;
  const submitsNow = today.submits_day;
  const submitsThen = path.result.submits_day;
  const campaigns = (n: number) => `${n} campaign${n === 1 ? '' : 's'}`;

  const sentence =
    `In “${path.label}”, we will take ${dollars(outOf)} a day out of ${campaigns(less.length)} `
    + `and put ${dollars(into)} a day into ${campaigns(more.length)}. `
    + `The total spend goes from ${dollars(today.spend_day)} a day to ${dollars(path.result.spend_day)} a day. `
    + (Math.abs(submitsThen - submitsNow) < 0.01
      ? `We still send ${twoDecimal(submitsThen)} applications a day, the same as today.`
      : `We send ${twoDecimal(submitsThen)} applications a day, instead of ${twoDecimal(submitsNow)} today.`);

  const columns: DataTableColumn<SimMove>[] = [
    {
      key: 'campaign',
      header: <Heading label="Campaign" info="The campaign, with its channel under it. Leads that came in without a campaign name are on a line called (no campaign), and each channel has its own such line. The campaigns that are too small to judge one by one are added together on one line." />,
      name: 'Campaign',
      width: 320,
      sticky: true,
      render: (m) => (
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
            {m.pooled ? 'All the small campaigns together' : m.campaign}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, whiteSpace: 'normal' })}>
            {m.pooled ? 'Every campaign with too few submits to judge on its own' : m.channel}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'today',
      header: <Heading label="Spend a day today" info="What we spend on this campaign in a day today, on average, over the dates above." />,
      numeric: true,
      render: (m) => dollars(m.spend_today),
    },
    {
      key: 'path',
      header: <Heading label="Spend a day in this scenario" info="What we would spend on this campaign in a day in the scenario you picked. A yellow tag means this is more than we have ever spent on it in a month, so we have not tried spending this much before." />,
      numeric: true,
      render: (m) => (
        <Stack spacing={0.25} alignItems="flex-end">
          <span>{dollars(m.spend_path)}</span>
          {m.beyond_what_it_has_run && <StatusChip tone="warning" label="More than we have ever spent" />}
        </Stack>
      ),
    },
    {
      key: 'change',
      header: <Heading label="Change a day" info="How much more or less money this campaign gets a day in the scenario, compared with today. A change smaller than a dollar a day, or than one percent, counts as no change." />,
      numeric: true,
      render: (m) => (
        <Box component="span" sx={(t) => ({ fontWeight: 600, color: m.direction === 'same' ? t.tokens.text.tertiary : t.tokens.text.primary })}>
          {m.direction === 'same' ? 'No change' : signed(m.change)}
        </Box>
      ),
    },
    {
      key: 'submits',
      header: <Heading label="Submits a day" info="How many applications this campaign's leads send a day: today, and then in the scenario. It shows why the money moves: a campaign gets more money when each dollar there buys more submits." />,
      numeric: true,
      render: (m) => (
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
          {twoDecimal(m.submits_today)}
          <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary, mx: 0.5 })}>→</Box>
          {twoDecimal(m.submits_path)}
        </Box>
      ),
    },
  ];

  const groups = [
    // The table puts the count beside each heading itself.
    { key: 'more', title: 'Campaigns that get more money', rows: more },
    { key: 'less', title: 'Campaigns that get less money', rows: less },
    { key: 'same', title: 'Campaigns that stay as they are', rows: same },
  ].filter((g) => g.rows.length > 0);

  const table = (fill: boolean) => (
    <Stack spacing={1.5} sx={fill ? { height: '100%', minHeight: 0 } : undefined}>
      <Typography variant="body1" sx={{ px: 2, pt: 1.5 }} aria-live="polite">{sentence}</Typography>
      <DataTable
        ariaLabel={`Where the money moves: ${path.label}`}
        columns={columns}
        groups={groups}
        getRowKey={(m) => m.key}
        maxHeight={fill ? undefined : 520}
        fill={fill}
      />
    </Stack>
  );

  return (
    <Panel
      title="Where the money moves"
      subtitle="For the scenario you pick: how much money each campaign gains or loses, a day."
      tools={
        <>
          <SelectMenu
            label="Scenario"
            value={path.key}
            options={moving.map((p) => ({ value: p.key, label: p.label }))}
            onChange={setPicked}
          />
        </>
      }
      actions={
        <>
          <DownloadButton
            formats={[{
              key: 'xlsx',
              label: 'Excel (.xlsx)',
              description: 'Every campaign in this scenario',
              onDownload: () => onDownload(path.key),
            }]}
            tooltip="Download where the money moves as Excel"
            loading={downloading}
            disabled={loading}
          />
        </>
      }
      loading={loading && !payload}
      loadingHeight={360}
      // The controls stay top-right and the long subtitle wraps instead, so
      // the header keeps one shape whichever scenario is picked.
      flush
      footer={
        problem ?? (
          <Typography variant="caption" component="span">
            (no campaign) means the leads that came in without a campaign name. Each channel has its own such line, and we move money into or out of it like any other campaign.
          </Typography>
        )
      }
      expanded={{ content: table(true) }}
    >
      {table(false)}
    </Panel>
  );
}
